'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import BackToTopButton from '../components/BackToTopButton';
import AshaIcon from '@/components/ui/AshaIcon';
import { Phone, Mail, MapPin, Clock, ChevronDown, ArrowRight, CalendarPlus } from 'lucide-react';

const faqs = [
{
  id: 'faq-referral',
  question: 'Do I need a physician referral to start physical therapy?',
  answer:
  'In Illinois, patients can receive direct access physical therapy for up to 30 days without a physician referral under certain conditions. However, many insurance plans still require a referral for coverage. We recommend calling us first — our team will verify your specific insurance requirements at no cost.'
},
{
  id: 'faq-insurance',
  question: 'What insurance plans does ASHA accept?',
  answer:
  'We accept most major insurance plans including Blue Cross Blue Shield, Aetna, Cigna, UnitedHealthcare, Medicare, Medicaid, and most workers\' compensation carriers. We also work with auto insurance for motor vehicle accident injuries. Contact your preferred clinic location for a complete list of accepted plans.'
},
{
  id: 'faq-first-visit',
  question: 'What should I expect at my first appointment?',
  answer:
  'Your initial evaluation is 60–90 minutes with a licensed therapist. They will review your medical history, perform a comprehensive movement and strength assessment, discuss your goals, and begin developing your personalized treatment plan. Wear comfortable, loose-fitting clothing that allows access to the area being treated.'
},
{
  id: 'faq-sessions',
  question: 'How many sessions will I need?',
  answer:
  'The number of sessions depends on your diagnosis, severity, and individual response to treatment. Most outpatient PT programs range from 8–24 sessions over 4–12 weeks. After your evaluation, your therapist will provide a recommended treatment plan, which is reviewed and adjusted based on your progress at each visit.'
},
{
  id: 'faq-home',
  question: 'Will I receive a home exercise program?',
  answer:
  'Yes — a home exercise program (HEP) is a core component of every ASHA treatment plan. Exercises are provided digitally through our patient portal with video demonstrations, dosage instructions, and a tracking log. Completing your HEP consistently between sessions significantly accelerates recovery outcomes.'
},
{
  id: 'faq-timeline',
  question: 'How long does recovery typically take after surgery?',
  answer:
  'Recovery timelines vary significantly by procedure. Total knee replacement: 8–12 weeks to independent ambulation, 4–6 months to full function. ACL reconstruction: 6–12 months to return to sport. Rotator cuff repair: 4–6 months for full strength return. Your surgeon\'s protocol and your individual healing rate are the primary determinants.'
},
{
  id: 'faq-cancel',
  question: 'What is the cancellation policy?',
  answer:
  'We ask for at least 24 hours\' notice for cancellations. Late cancellations and no-shows may be subject to a $35 fee, which is not covered by insurance. We understand that emergencies happen — please call us as soon as possible if you need to reschedule.'
},
{
  id: 'faq-telehealth',
  question: 'Does ASHA offer telehealth physical therapy?',
  answer:
  'Yes — we offer telehealth PT sessions for appropriate conditions, including post-surgical follow-ups after the initial in-person evaluation, home exercise program instruction, and certain chronic pain management protocols. Coverage varies by insurance plan. Ask your therapist whether telehealth is suitable for your treatment plan.'
}];


const contactMethods = [
{
  id: 'contact-phone',
  icon: <Phone size={22} />,
  title: 'Call Us',
  detail: '(312) 555-0100',
  sub: 'Mon–Fri 8:00 AM – 6:00 PM',
  href: 'tel:3125550100',
  cta: 'Call Now'
},
{
  id: 'contact-email',
  icon: <Mail size={22} />,
  title: 'Email Us',
  detail: 'info@asharecoverygroup.com',
  sub: 'Response within 1 business day',
  href: 'mailto:info@asharecoverygroup.com',
  cta: 'Send Email'
},
{
  id: 'contact-location',
  icon: <MapPin size={22} />,
  title: 'Find a Clinic',
  detail: '18 Locations',
  sub: 'Across Chicagoland',
  href: '/locations',
  cta: 'View Locations'
},
{
  id: 'contact-hours',
  icon: <Clock size={22} />,
  title: 'Office Hours',
  detail: 'Mon–Sat',
  sub: '6:30 AM – 8:00 PM (varies by location)',
  href: '/locations',
  cta: 'Check Hours'
}];


interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  preferredContact: string;
}

export default function ContactPage() {
  const [openId, setOpenId] = useState<string | null>('faq-referral');
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: 'general',
    message: '',
    preferredContact: 'email'
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <Image
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1265ae09d-1777666541663.png"
              alt="Friendly ASHA Recovery Group staff member ready to assist patients at the front desk"
              fill
              className="object-cover object-center"
              priority
              sizes="100vw" />
            
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,42,67,0.92) 0%, rgba(10,42,67,0.75) 60%, rgba(27,175,191,0.4) 100%)' }} />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6"
              style={{ background: 'rgba(27,175,191,0.2)', color: 'var(--secondary)', border: '1px solid rgba(27,175,191,0.3)' }}>
                Get In Touch
              </div>
              <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                We&apos;re Here to Help<br />
                <span style={{ color: 'var(--secondary)' }}>Your Recovery</span>
              </h1>
              <p className="text-xl text-white/70 leading-relaxed max-w-2xl">
                Whether you&apos;re a new patient, an existing patient with questions, or a physician looking to refer — our team is ready to assist you.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-14 bg-white border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
              {contactMethods.map((method) =>
              <a key={method.id} href={method.href}
              className="group flex items-start gap-4 bg-white border border-border rounded-2xl p-6 card-shadow hover:card-shadow-md transition-all duration-200 hover:-translate-y-0.5">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                    {method.icon}
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">{method.title}</p>
                    <p className="font-bold text-primary">{method.detail}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{method.sub}</p>
                    <p className="text-xs font-semibold mt-2 flex items-center gap-1 group-hover:gap-2 transition-all"
                  style={{ color: 'var(--secondary)' }}>
                      {method.cta} <ArrowRight size={12} />
                    </p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Contact Form + FAQ */}
        <section className="py-20 lg:py-28" style={{ background: 'var(--background)' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
              {/* Contact Form */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
                style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                  Send a Message
                </div>
                <h2 className="text-4xl font-extrabold text-primary leading-tight mb-8">
                  Request an Appointment or Ask a Question
                </h2>

                {submitted ?
                <div className="bg-white border border-border rounded-2xl p-10 card-shadow text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'var(--teal-light)' }}>
                      <AshaIcon name="success-recovery" size={32} color="var(--teal-mid)" />
                    </div>
                    <h3 className="text-2xl font-bold text-primary mb-3">Message Sent!</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      Thank you for reaching out. A member of our team will contact you within one business day.
                    </p>
                    <button
                    onClick={() => setSubmitted(false)}
                    className="btn-primary px-6 py-3 rounded-xl text-sm">
                      Send Another Message
                    </button>
                  </div> :

                <form onSubmit={handleSubmit} className="bg-white border border-border rounded-2xl p-8 card-shadow space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">First Name</label>
                        <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{ '--tw-ring-color': 'var(--secondary)' } as React.CSSProperties}
                        placeholder="Jane" />
                      
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">Last Name</label>
                        <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        placeholder="Smith" />
                      
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Email Address</label>
                      <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                      placeholder="jane@example.com" />
                    
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Phone Number</label>
                      <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                      placeholder="(312) 555-0000" />
                    
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Subject</label>
                      <select
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-white">
                        <option value="general">General Inquiry</option>
                        <option value="appointment">Request an Appointment</option>
                        <option value="insurance">Insurance Question</option>
                        <option value="referral">Physician Referral</option>
                        <option value="billing">Billing Question</option>
                        <option value="telehealth">Telehealth Inquiry</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Message</label>
                      <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us how we can help you..." />
                    
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Preferred Contact Method</label>
                      <div className="flex gap-4">
                        {['email', 'phone'].map((method) =>
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                            <input
                          type="radio"
                          name="preferredContact"
                          value={method}
                          checked={formData.preferredContact === method}
                          onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                          className="accent-secondary" />
                        
                            <span className="text-sm text-foreground capitalize">{method}</span>
                          </label>
                      )}
                      </div>
                    </div>
                    <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-base">
                      <CalendarPlus size={18} />
                      Send Message
                    </button>
                    <p className="text-xs text-muted-foreground text-center">
                      We respond within 1 business day. For urgent matters, please call (312) 555-0100.
                    </p>
                  </form>
                }
              </div>

              {/* FAQ */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
                style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                  <AshaIcon name="faq-support" size={14} color="var(--teal-mid)" />
                  Frequently Asked Questions
                </div>
                <h2 className="text-4xl font-extrabold text-primary leading-tight mb-8">
                  Common Questions
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq) =>
                  <div key={faq.id}
                  className={`rounded-2xl border overflow-hidden transition-all duration-200 ${
                  openId === faq.id ? 'border-secondary/30 card-shadow-md' : 'border-border card-shadow'}`
                  }>
                      <button
                      onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                      className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-muted/30 transition-colors duration-150"
                      aria-expanded={openId === faq.id}>
                        <span className="font-semibold text-primary text-sm leading-snug pr-4">
                          {faq.question}
                        </span>
                        <ChevronDown
                        size={18}
                        className={`flex-shrink-0 text-muted-foreground transition-transform duration-200 ${
                        openId === faq.id ? 'rotate-180' : ''}`
                        } />
                      
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openId === faq.id ? 'max-h-64' : 'max-h-0'}`}>
                        <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Physician Referral CTA */}
        <section className="py-20 lg:py-24 bg-white">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
            <div className="rounded-3xl p-10 lg:p-16 text-center" style={{ background: 'var(--primary)' }}>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5">
                Are You a Referring Physician?
              </h2>
              <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
                Submit referrals online, track patient progress, and access discharge summaries through our secure physician portal.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/referrals" className="btn-secondary flex items-center gap-2 px-6 py-3.5 rounded-2xl text-base">
                  <AshaIcon name="submit-referral" size={18} color="white" />
                  Submit a Referral
                </Link>
                <Link href="/sign-up-login-screen" className="flex items-center gap-2 px-6 py-3.5 rounded-2xl border border-white/30 text-white hover:bg-white/10 transition-colors font-semibold text-base">
                  Physician Portal Login
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
      <BackToTopButton />
    </div>);

}